import { passwordStrength, resetPasswordSchema } from '@insightai/auth'
import { Button, Field, Input, cn } from '@insightai/ui'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { config } from '../../config'
import { mockResetPassword } from '../../auth/mockAuth'
import { useZodForm } from '../../auth/useZodForm'
import { AuthLayout } from './AuthLayout'

/** M00-22 — reset password, reached from the emailed link. */
export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const [done, setDone] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const form = useZodForm(resetPasswordSchema, { password: '', confirmPassword: '' })
  const strength = passwordStrength(form.values.password)

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null)

    if (config.prototypeMode) {
      const { ok } = await mockResetPassword(token)
      if (ok) setDone(true)
      else
        setServerError(
          'That reset link has expired or has already been used. Request a new one.',
        )
      return
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: values.password }),
      })
      if (!response.ok) {
        setServerError(
          response.status === 400 || response.status === 410
            ? 'That reset link has expired or has already been used. Request a new one.'
            : 'We could not reset your password. Try again shortly.',
        )
        return
      }
      setDone(true)
    } catch {
      setServerError('We could not reach the server. Check your connection and try again.')
    }
  })

  // An absent token means the page was opened directly rather than from the email.
  if (!token) {
    return (
      <AuthLayout title="Reset link required">
        <div className="flex flex-col gap-3">
          <p className="text-body-sm text-text-secondary">
            This page needs the link from your reset email.
          </p>
          <Button asChild variant="secondary">
            <Link to="/auth/forgot-password">Request a new link</Link>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Choose a new password">
      {done ? (
        <div role="status" className="flex flex-col gap-3">
          <p className="text-body-sm text-text-primary">Your password has been updated.</p>
          <Button asChild>
            <Link to="/auth/login">Sign in</Link>
          </Button>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field label="New password" required error={form.errors.password}>
            <Input
              type="password"
              autoComplete="new-password"
              autoFocus
              value={form.values.password}
              onChange={(event) => form.setField('password', event.target.value)}
            />
          </Field>

          <div className="flex items-center gap-2">
            <div
              role="meter"
              aria-label="Password strength"
              aria-valuenow={strength.score}
              aria-valuemin={0}
              aria-valuemax={4}
              aria-valuetext={strength.label}
              className="flex h-1.5 flex-1 gap-1"
            >
              {[0, 1, 2, 3].map((index) => (
                <span
                  key={index}
                  className={cn(
                    'h-full flex-1 rounded-full',
                    index < strength.score
                      ? strength.score >= 3
                        ? 'bg-state-verified'
                        : 'bg-state-warning'
                      : 'bg-surface-inset',
                  )}
                />
              ))}
            </div>
            <span className="text-caption text-text-secondary">{strength.label}</span>
          </div>

          <Field label="Confirm new password" required error={form.errors.confirmPassword}>
            <Input
              type="password"
              autoComplete="new-password"
              value={form.values.confirmPassword}
              onChange={(event) => form.setField('confirmPassword', event.target.value)}
            />
          </Field>

          {serverError && (
            <p role="alert" className="text-caption text-state-critical">
              {serverError}
            </p>
          )}

          <Button type="submit" loading={form.submitting}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
